// src/pages/auth/Signup.js
import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Paper,
  Divider,
} from '@mui/material';
import GoogleAuthButton from './GoogleAuthButton';
import { useLoader } from '../../contexts/LoaderContext';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();

  const savedFormData = sessionStorage.getItem('signup_form_data');
  const initialFormData = savedFormData
    ? JSON.parse(savedFormData)
    : {
        company_name: '',
        name: '',
        email: '',
        mobile: '',
        tnc_consent: false,
      };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({
    company_name: '',
    name: '',
    email: '',
    mobile: '',
    tnc_consent: '',
  });
  const [agreeTerms, setAgreeTerms] = useState(initialFormData.tnc_consent || false);
  const [loading, setLoading] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const { openSuccessModal } = useModal();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData({ ...formData, [name]: digitsOnly });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleTermsChange = (e) => {
    const checked = e.target.checked;
    setAgreeTerms(checked);
    setFormData((prev) => ({ ...prev, tnc_consent: checked }));
    if (checked && errors.tnc_consent) {
      setErrors((prev) => ({ ...prev, tnc_consent: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      company_name: '',
      name: '',
      email: '',
      mobile: '',
      tnc_consent: '',
    };
    let isValid = true;

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
      isValid = false;
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }
    if (!formData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }
    if (!formData.mobile) {
      newErrors.mobile = 'Mobile number is required';
      isValid = false;
    } else if (!/^\d{10}$/.test(formData.mobile.replace(/\D/g, ''))) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
      isValid = false;
    }
    if (!formData.tnc_consent) {
      newErrors.tnc_consent = 'You must agree to the Terms and Privacy Policy';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    showLoader();
    try {
      const response = await signup(formData);
      const result = response.data;
      hideLoader();
      openSuccessModal(
        'Account Created Successfully',
        result.message || 'Your account has been created.',
        () => navigate('/auth/login')
      );
    } catch (error) {
      const errorData = error?.response?.data;
      if (errorData && typeof errorData === 'object') {
        const fieldMapping = {
          username: 'email',
          email: 'email',
          name: 'name',
          mobile: 'mobile',
          company_name: 'company_name',
        };
        const serverErrors = {};
        Object.entries(errorData).forEach(([field, message]) => {
          const frontendField = fieldMapping[field] || field;
          serverErrors[frontendField] = Array.isArray(message) ? message[0] : message;
        });
        setErrors((prev) => ({ ...prev, ...serverErrors }));
      } else {
        const errorMessage =
          errorData?.message ||
          errorData?.detail ||
          'An unexpected error occurred. Please try again.';
        setErrors((prev) => ({ ...prev, tnc_consent: errorMessage }));
      }
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const inputHeight = 50;
  const fieldSx = (fieldError) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#fff',
      minHeight: inputHeight,
      '& fieldset': {
        borderColor: fieldError ? '#d32f2f' : '#e0e0e0',
        borderWidth: fieldError ? 2 : 1,
        borderRadius: '8px',
      },
      '&:hover fieldset': {
        borderColor: fieldError ? '#d32f2f' : '#bdbdbd',
      },
      '&.Mui-focused fieldset': {
        borderColor: fieldError ? '#d32f2f' : '#059669',
        borderWidth: 2,
      },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
    },
  });

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 520,
        minWidth: 320,
        mx: 'auto',
        p: { xs: 2, sm: 2.5 },
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        backgroundColor: '#fff',
        boxShadow: '0 2px 24px rgba(0,0,0,0.06)',
      }}
    >
      <Typography
        variant="h6"
        component="h1"
        align="center"
        sx={{
          fontWeight: 700,
          color: '#1f2937',
          fontSize: '1.25rem',
          mb: 1.5,
        }}
      >
        Start Your 7-Days Free Trial Now!
      </Typography>

      <GoogleAuthButton action="signup" />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
        <Divider sx={{ flex: 1, borderColor: '#e0e0e0' }} />
        <Typography variant="body2" sx={{ color: '#9e9e9e', fontWeight: 600, letterSpacing: 0.5 }}>
          OR
        </Typography>
        <Divider sx={{ flex: 1, borderColor: '#e0e0e0' }} />
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          required
          fullWidth
          id="company_name"
          label="Company Name"
          name="company_name"
          placeholder="Company Name*"
          size="medium"
          value={formData.company_name}
          onChange={handleChange}
          error={!!errors.company_name}
          helperText={errors.company_name}
          sx={{ mb: 1.5, ...fieldSx(errors.company_name) }}
          variant="outlined"
        />

        <TextField
          required
          fullWidth
          id="name"
          label="Name"
          name="name"
          placeholder="Name*"
          size="medium"
          value={formData.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
          sx={{ mb: 1.5, ...fieldSx(errors.name) }}
          variant="outlined"
        />

        <TextField
          required
          fullWidth
          id="email"
          label="Email"
          name="email"
          placeholder="Email*"
          type="email"
          autoComplete="email"
          size="medium"
          value={formData.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
          sx={{ mb: 1.5, ...fieldSx(errors.email) }}
          variant="outlined"
        />

        <TextField
          required
          fullWidth
          id="mobile"
          label="Mobile Number"
          name="mobile"
          placeholder="Mobile Number*"
          size="medium"
          value={formData.mobile}
          onChange={handleChange}
          error={!!errors.mobile}
          helperText={errors.mobile}
          inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
          sx={{ mb: 1.5, ...fieldSx(errors.mobile) }}
          variant="outlined"
        />

        <Box sx={{ mt: 1, mb: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreeTerms}
                onChange={handleTermsChange}
                size="small"
                sx={{
                  color: '#757575',
                  '&.Mui-checked': { color: '#007050' },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#424242', fontSize: '0.875rem' }}>
                I agree to the{' '}
                <Link
                  href="https://salesgro.ai/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                  sx={{ color: '#3B82F6', fontWeight: 500, fontSize: '0.875rem' }}
                >
                  Terms & Privacy Policy
                </Link>
                .
              </Typography>
            }
            sx={{ alignItems: 'center', m: 0 }}
          />
          {errors.tnc_consent && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 3.5 }}>
              {errors.tnc_consent}
            </Typography>
          )}
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            minHeight: 52,
            py: 1.5,
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 700,
            textTransform: 'none',
            background: 'linear-gradient(to right, #34D399 0%, #059669 100%)',
            boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)',
            '&:hover': {
              background: 'linear-gradient(to right, #2dd4a0 0%, #047857 100%)',
              boxShadow: '0 4px 12px rgba(5, 150, 105, 0.35)',
            },
            '&:disabled': {
              background: '#bdbdbd',
              color: '#fff',
            },
          }}
        >
          {loading ? 'Creating account...' : 'Start free trial'}
        </Button>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#4b5563', fontSize: '0.875rem' }}>
            Already have an account?{' '}
            <Link
              component={RouterLink}
              to="/auth/login"
              underline="none"
              sx={{ color: '#3B82F6', fontWeight: 700, fontSize: '0.875rem', '&:hover': { textDecoration: 'underline' } }}
            >
              Log in
            </Link>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default Signup;
